<template>
    <table class="RPDatatable">
        <thead>
            <!--
                Triggered when user changes sort column / direction
                @event set-sort
                @property {object} sort An object with `key` and `direction` properties
            -->
            <HeaderRow
                :headers="filteredHeaders"
                :status="status"
                :allow-multiselect="allowMultiselect"
                :has-v-model="hasVModel"
                :show-action-cell="showAction"
                :sort-property="sortProperty"
                :sort="sort"
                :sort-direction="sortDirection"
                :is-all-checked="isAllChecked"
                :is-some-checked="isSomeChecked"
                @set-sort="e => $emit('set-sort', e)"
                @toggle-all="toggleAll"
                @deselect="deselect"
            />
        </thead>
        <tbody>
            <template v-if="!isLoading && !tableData.length">
                <tr class="DataRow DataRow--no-results">
                    <!-- @slot Content to display when there are no rows -->
                    <slot name="noResults">
                        <span :style="{'margin-left': '0.5rem'}">No Results Found</span>
                    </slot>
                </tr>
            </template>
            <template v-else>
                <DataRow
                    v-for="(row, index) in formattedRows"
                    :key="`${JSON.stringify(row)}-${index}`"
                    :headers="filteredHeaders"
                    :row="row"
                    :disabled="!!row.disabled"
                    :table-type="tableType"
                    :has-v-model="hasVModel"
                    :allow-multiselect="allowMultiselect"
                    :show-action-cell="showAction"
                    :show-extra-options="showExtraOptions"
                    :is-selected="isSelected(row.id)"
                    :status="rowStatus(row.id)"
                    @toggle-select="id => toggleSelect(id)"
                    @row-select="id => rowSelect(id)"
                >
                    <template
                        v-for="header in filteredHeaders"
                        v-slot:[header.key]="{prop, rowData}"
                    >
                        <!--
                            @slot Replace the content of a table cell entirely. There is a separate slot for each column, using the header key as the slot name.
                                @binding {} prop The key of the current column
                                @binding {object} rowData All data for the current row
                        -->
                        <slot
                            :name="header.key"
                            :prop="prop"
                            :rowData="rowData"
                        >
                            {{ rowData[prop] }}
                        </slot>
                    </template>
                    <template v-slot:row="{ rowData }">
                        <!--
                            @slot Content to display in the 'action' column
                                @binding {} rowId The ID of the current row
                        -->
                        <slot
                            :rowData="rowData"
                            name="row"
                        >
                        </slot>
                    </template>
                    <template v-slot:actionSlot="{ rowId }">
                        <!--
                            @slot Content to display in the 'action' column
                                @binding {} rowId The ID of the current row
                        -->
                        <slot
                            :rowId="rowId"
                            name="actionSlot"
                        >
                        </slot>
                    </template>
                    <template v-slot:extraSlot="{ rowId, rowData}">
                        <slot
                            :rowId="rowId"
                            :rowData="rowData"
                            name="extraSlot"
                        >
                        </slot>
                    </template>
                    <template v-slot:extraOptions="{ rowId, rowData}">
                        <!--
                            @slot Content to display in the 'extra options' dropdown
                                @binding {} rowId The ID of the current row
                        -->
                        <slot
                            :rowId="rowId"
                            :rowData="rowData"
                            name="extraOptions"
                        >
                        </slot>
                    </template>
                    <template v-slot:modal>
                        <slot name="modal"></slot>
                    </template>
                </DataRow>
            </template>
        </tbody>
    </table>
</template>
<script>
import HeaderRow from './components/HeaderRow.vue';
import DataRow from './components/DataRow.vue';
import stat from 'status/index';
import {NavigationMenuHorizontal} from "@reviewpush/streamline-regular/lib/interface-essential/Menu";
import {has, cloneDeep} from 'lodash';

export default {
    name: 'RPDatatable',
    components: {
        DataRow,
        HeaderRow,
    },
    props: {
        /**
         * The currently selected row(s) in the table. To allow multiselect on the table,
         * pass an array as the initial value.
         * @model
         */
        value: {
            type: [Array, String, Number],
            default: () => null,
        },
        /**
         * An array of objects with `key`, `value`, and `sortable` properties.
         * E.x.: `[{key: 1, label: 'One', sortable: true}]`
         */
        headers: {
            type: Array,
            required: true,
        },
        /**
         * An array of objects with keys corresponding to the header row keys.
         * Each item must also have an `id` key.
         */
        tableData: {
            type: Array,
            required: true,
            validator: arr => arr.findIndex(a => !has(a, 'id')) >= -1,
        },
        sortProperty: {
            type: Object,
            default: () => ({key: 'sort_by', direction: 'sort_direction'})
        },
        /**
         * The key of the default column to sort on
         */
        sort: {
            type: String,
            default: '',
        },
        /**
         * @values asc, desc
         */
        sortDirection: {
            type: String,
            default: 'asc',
            validator: val => ['asc', 'desc'].indexOf(val) !== -1,
        },
        /**
         * @values page, card
         */
        tableType: {
            type: String,
            default: 'page',
            validator: val => ['page', 'card'].indexOf(val) !== -1,
        },
        /**
         * Whether to show the ID column
         */
        hideId: {
            type: Boolean,
            default: false,
        },
        /**
         * Whether to display the 'extra options' icon. When the icon is clicked,
         * the content of the `extraOptions` slot is displayed in a dropdown.
         */
        showExtraOptions: {
            type: Boolean,
            default: false,
        },
        /**
         * Whether to show an extra column for action buttons. This column
         * will display the content of the `actionSlot` prop.
         * This column will also be shown if the `showExtraOptions` prop is `true`.
         */
        showActionCell: {
            type: Boolean,
            default: false,
        },
        /**
         * Number of rows to display in `loading` state
         * TODO: revisit this
         */
        limit: {
            type: Number,
            default: 5,
        },
        status: {
            type: String,
            default: stat.STANDBY,
        }
    },
    data(){
        return {
            options: {
                filterable: false,
                footerHeadings: false,
                texts: {
                    count: '',
                }
            },
            extraOptionsId: '',
            horizontalMenuIcon: NavigationMenuHorizontal,
        };
    },
    computed: {
        hasVModel(){
            return this.value !== null;
        },
        allowMultiselect() {
            return this.tableData.length !== 0
                && this.hasVModel
                && typeof this.value !== 'string'
                && typeof this.value !== 'number';
        },
        showAction(){
            return this.showActionCell
              || this.showExtraOptions;
        },
        isLoading(){
            return this.status === stat.LOADING;
        },
        isAllChecked(){
            return this.allowMultiselect
                    && this.value.length === this.tableData.length
                    && this.tableData.length > 0;
        },
        isSomeChecked(){
            return this.allowMultiselect
                    && !this.isAllChecked
                    && this.value.length > 0
                    && this.tableData.length > 0;
        },
        filteredHeaders(){
            if(this.hideId) {
                return cloneDeep(this.headers).filter(header => header.key !== 'id');
            }
            return this.headers;
        },
        formattedRows(){
            if(this.isLoading) {
                return new Array(this.limit).fill({}, 0, this.limit);
            }
            return this.tableData.map(d => {
                return {
                    status: stat.STANDBY,
                    disabled: has(d, 'disabled') ? d.disabled : false,
                    ...d
                };
            });
        }
    },
    methods: {
        isSelected(id){
            return this.value !== null
                && this.allowMultiselect
                ? this.value.includes(id)
                : this.value === id;
        },
        toggleAll(){
            /**
             * Used for v-model
             * @event input
             */
            this.$emit('input', this.isAllChecked ? [] : this.tableData.map(d => d.id));
            this.$emit(
                'change',
                this.tableData.map(d => d.id).filter(d => !this.value.includes(d))
            );
        },
        deselect(){
            /**
             * Used for v-model
             * @event input
             */
            this.$emit('input', []);
        },
        toggleSelect(id){
            if(this.hasVModel){
                this.$emit('change', id);
                if(this.allowMultiselect){
                    let valArr = cloneDeep(this.value);
                    if(this.value.includes(id)) {
                        valArr = valArr.filter(i => i !== id);
                    } else {
                        valArr.push(id);
                    }
                    this.$emit('input', valArr);
                } else {
                    this.$emit('input', id === this.value ? '' : id );
                }
            }
        },
        rowSelect(row){
            if(this.hasVModel && !this.allowMultiselect) {
                this.$emit('input', row.id === this.value ? '' : row.id);
            }
            /**
             * Triggered when a row is selected
             * @event row-select
             * @property {} id The selected row's ID
             */
            this.$emit('row-select', row);
        },
        rowStatus(rowId){
            const s = this.status;
            if(this.status === stat.SAVING) {
                if((this.allowMultiselect && this.value.findIndex(id => id === rowId) > -1)
                  ||(!this.allowMultiselect && this.value === rowId)) {
                    return stat.LOADING;
                }
            }
            return s;
        },
    },
};
</script>

<style lang="scss">
    .RPDatatable {
        width: 100%;
        border-collapse: separate;
        text-align: left;
        border-spacing: 0 0.25rem;
    }
</style>

<docs>
The datatable component displays rows of data. It doesn't handle fetching or sorting data on its own. Instead,
the parent component fetches data and passes it to the table, while the table emits events when the user selects
rows or when columns are sorted. Pagination is handled by the parent.

```[import](./RPDatatableExample.vue)
```
</docs>
